import { useMemo } from "react"
import { Step1Data } from "../types"

interface EditableTargets {
  calories: number
  carbs: number
  protein: number
  fats: number
}

const MEAL_NAME_MAPPING: Record<string, string> = {
  "Once_awake": "once_awake",
  "Once awake": "once_awake",
  "Early_Morning": "early_morning",
  "Early Morning": "early_morning",
  "Breakfast": "breakfast",
  "Morning_Snack": "morning_snack",
  "Morning Snack": "morning_snack",
  "Mid Morning": "morning_snack",
  "Lunch": "lunch",
  "Evening_Snack": "evening_snack",
  "Evening Snack": "evening_snack",
  "Dinner": "dinner",
  "Post_Dinner": "post_dinner",
  "Post Dinner": "post_dinner",
  "Bed_time": "bed_time",
  "Bed time": "bed_time",
}

const MEAL_CALORIE_MULTIPLIERS = {
  once_awake: 0.3,
  early_morning: 0.4,
  breakfast: 1.2,
  morning_snack: 0.5,
  lunch: 1.3,
  evening_snack: 0.5,
  dinner: 1.2,
  post_dinner: 0.3,
  bed_time: 0.2,
}

const MEAL_DISPLAY_NAMES: Record<string, string> = {
  once_awake: "Once Awake",
  early_morning: "Early Morning",
  breakfast: "Breakfast",
  morning_snack: "Morning Snack",
  lunch: "Lunch",
  evening_snack: "Evening Snack",
  dinner: "Dinner",
  post_dinner: "Post Dinner",
  bed_time: "Bed Time",
}

const DEFAULT_VALUES = {
  demographics: {
    gender: "M" as const,
    age: 25,
    height_cm: 170,
    weight_kg: 70,
  },
  goal: {
    type: "maintain" as const,
    macro_split_pct: { carbs: 50, protein: 25, fats: 25 },
  },
  behavior_signals: {
    late_sleeper: false,
    irregular_breakfast: false,
    evening_tea_snack: false,
    late_night_hunger: false,
    fruit_inconsistent: false,
    sleep_disturbed: false,
    activity_level: "moderate" as const,
  },
  meal_types: {
    once_awake: false,
    early_morning: false,
    breakfast: true,
    morning_snack: false,
    lunch: true,
    evening_snack: false,
    dinner: true,
    post_dinner: false,
    bed_time: false,
  }
}

export const useHealthProfileCalculations = (
  formData: Partial<Step1Data>,
  editableTargets: EditableTargets | null,
  apiData?: any
) => {
  // Helper function to get profile data from API
  const getProfileData = useMemo(() => {
    return () => {
      if (apiData?.diet_plan?.patient_profile_v0) {
        return apiData.diet_plan.patient_profile_v0
      }
      if (apiData?.top_5_regenerated_diet_plans?.[0]?.patient_profile_v0) {
        return apiData.top_5_regenerated_diet_plans[0].patient_profile_v0
      }
      if (apiData?.patient_profile_v0) {
        return apiData.patient_profile_v0
      }
      return null
    }
  }, [apiData])

  // Map API data to component data structure
  const mapApiDataToFormData = useMemo(() => {
    return (apiData: any) => {
      const profile = getProfileData()
      if (!profile) return null

      const demographics = {
        gender: profile.demographics?.gender || DEFAULT_VALUES.demographics.gender,
        age: profile.demographics?.age || DEFAULT_VALUES.demographics.age,
        height_cm: profile.demographics?.height_cm || DEFAULT_VALUES.demographics.height_cm,
        weight_kg: profile.demographics?.weight_kg || DEFAULT_VALUES.demographics.weight_kg,
      }

      const conditions = profile.conditions || []

      const goal = {
        type: profile.goal?.type || DEFAULT_VALUES.goal.type,
        macro_split_pct: profile.goal?.macro_split_pct || DEFAULT_VALUES.goal.macro_split_pct,
        goal_calories: profile.goal?.goal_calories,
        macro_targets_g: profile.goal?.macro_targets_g,
      }

      const behavior_signals = {
        late_sleeper: Boolean(profile.behavior_signals?.late_sleeper),
        irregular_breakfast: Boolean(profile.behavior_signals?.irregular_breakfast),
        evening_tea_snack: Boolean(profile.behavior_signals?.evening_tea_snack),
        late_night_hunger: Boolean(profile.behavior_signals?.late_night_hunger),
        fruit_inconsistent: Boolean(profile.behavior_signals?.fruit_inconsistent),
        sleep_disturbed: Boolean(profile.behavior_signals?.sleep_disturbed),
        activity_level: profile.behavior_signals?.activity_level || DEFAULT_VALUES.behavior_signals.activity_level,
      }

      const meal_types = { ...DEFAULT_VALUES.meal_types }
      const mealTypeAvailable = profile.meal_type_available || apiData.meal_type_available

      if (mealTypeAvailable) {
        Object.entries(mealTypeAvailable).forEach(([key, value]) => {
          const mappedKey = MEAL_NAME_MAPPING[key]
          if (mappedKey) {
            meal_types[mappedKey as keyof typeof meal_types] = Boolean(value)
          }
        })
      }

      return {
        demographics,
        conditions,
        goal,
        behavior_signals,
        meal_types,
        total_calorie_goal: profile.goal?.goal_calories,
        macro_targets_g: profile.goal?.macro_targets_g,
        meal_type_reasons: profile.meal_type_reasons,
        per_meal_kcal: profile.per_meal_kcal,
      }
    }
  }, [getProfileData])

  const calculateTotalCalories = useMemo(() => {
    return () => {
      if (editableTargets) return editableTargets.calories

      // Use API data if available
      if (apiData?.diet_plan?.personalized_diet_plan?.total_calories) {
        return apiData.diet_plan.personalized_diet_plan.total_calories as number
      }

      // Return 0 if no API data - calculation should be handled by useCalorieCalculation hook
      return 0
    }
  }, [editableTargets, apiData])

  const generateMealReasons = useMemo(() => {
    return () => {
      const profile = getProfileData()
      const apiReasons = profile?.meal_type_reasons

      if (!apiReasons) return {}

      const reasons: Record<string, string> = {}
      Object.entries(apiReasons).forEach(([apiMealName, reason]) => {
        const mappedMealName = MEAL_NAME_MAPPING[apiMealName]
        if (mappedMealName && formData.meal_types![mappedMealName as keyof typeof formData.meal_types]) {
          reasons[mappedMealName] = reason as string
        }
      })

      return reasons
    }
  }, [getProfileData, formData])

  const distributeMealCalories = useMemo(() => {
    return (totalCalories: number) => {
      const profile = getProfileData()
      const apiMealCalories = profile?.per_meal_kcal

      if (apiMealCalories) {
        const enabledMeals = Object.entries(formData.meal_types!).filter(([_, enabled]) => enabled)
        if (enabledMeals.length === 0) return {}

        const enabledMealCalories: Record<string, number> = {}
        let totalApiCalories = 0

        enabledMeals.forEach(([mealKey, _]) => {
          const apiMealName = Object.keys(apiMealCalories).find(apiName => 
            MEAL_NAME_MAPPING[apiName] === mealKey
          )
          
          if (apiMealName && apiMealCalories[apiMealName]) {
            const calories = apiMealCalories[apiMealName] as number
            enabledMealCalories[mealKey] = calories
            totalApiCalories += calories
          }
        })

        if (totalApiCalories > 0) {
          return enabledMealCalories
        }
      }

      // Fallback to calculated distribution
      const enabledMeals = Object.entries(formData.meal_types!).filter(([_, enabled]) => enabled)
      const mealCount = enabledMeals.length

      if (mealCount === 0) return {}

      const baseCalories = Math.round(totalCalories / mealCount)
      const distribution: Record<string, number> = {}

      enabledMeals.forEach(([meal, _]) => {
        const multiplier = MEAL_CALORIE_MULTIPLIERS[meal as keyof typeof MEAL_CALORIE_MULTIPLIERS] || 0.6
        distribution[meal] = Math.round(baseCalories * multiplier)
      })

      return distribution
    }
  }, [getProfileData, formData])

  return {
    mapApiDataToFormData,
    calculateTotalCalories,
    generateMealReasons,
    distributeMealCalories,
    constants: {
      MEAL_NAME_MAPPING,
      MEAL_CALORIE_MULTIPLIERS,
      MEAL_DISPLAY_NAMES,
      DEFAULT_VALUES,
    },
  }
}
